import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import { Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import { rankItem } from '@tanstack/match-sorter-utils';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';

import { useDevice } from '@hero/hooks';
import { clearObject } from '@hero/utils';

import { EmptyTableContent } from '../EmptyTableContent';
import { SearchInput } from '../SearchInput';

import { defaultEnabledList, defaultPagination } from './constant';
import { TBody, Thead } from './Core';
import { columnTypes, getDefaultColumn } from './DefaultColumns';
import { Header } from './Header/Header';
import { Pagination } from './Pagination/Pagination';
import * as S from './style';
import { TranslationContext } from './translation/translationContext';

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value?.searchTerm || '');

  addMeta({
    itemRank
  });

  return itemRank.passed;
};

function Table({
  data,
  loading,
  columns,
  updateData,
  setRearrangeData,
  rowSelection = {},
  setRowSelection,
  setPagination,
  pagination,
  pageCount,
  state,
  globalFilters,
  manualPagination,
  components = {},
  setGlobalFilters,
  filterOptions = [],
  useGlobalFuzzyFilter,
  rowHeight,
  enabled = defaultEnabledList,
  filterProps,
  searchProps,
  paginationProps,
  getRowId = row => {
    return row?.id;
  },
  translations = {},
  onRowClick,
  enableRowSelection,
  tableRef,
  emptyTableContent = {},
  renderSubRow,
  editMode = 'inline',
  sectionHeaderProps = {
    enabled: false
  }
}) {
  const [sorting, setSorting] = React.useState([]);
  const [expanded, setExpanded] = React.useState({});
  const [editing, setEditing] = React.useState({});

  const handleSave = useCallback(
    row => {
      const updatedRow = { ...row.original };
      row.getAllCells().forEach(cell => {
        if (cell.column.columnDef.editable) {
          const input = document.querySelector(`[data-cell-id="${cell.id}"] input`);

          if (input) {
            updatedRow[cell.column.id] = input.value;
          }
        }
      });

      // Call the updateData function with the updated row
      if (typeof updateData === 'function') {
        updateData(updatedRow);
      }

      // Remove from editing state
      setEditing(prev => {
        const newState = { ...prev };
        delete newState[row.original.id];
        return newState;
      });
    },
    [updateData]
  );

  const handleCancel = useCallback(row => {
    setEditing(prev => {
      const newState = { ...prev };
      delete newState[row.original.id];
      return newState;
    });
  }, []);

  const handleEdit = useCallback(row => {
    setEditing(prev => ({
      ...prev,
      [row.original.id]: true
    }));
  }, []);

  const { isMobile, isTablet, isDesktop } = useDevice();

  const screens = {
    mobile: isMobile,
    tablet: isTablet && !isMobile,
    desktop: isDesktop && !isMobile && !isTablet
  };

  const tableData = loading
    ? Array(pagination.pageSize || 10)
        .fill({})
        .map((item, index) => ({ id: index, contact: { id: index } }))
    : data;

  const defaultState = useMemo(() => {
    const mergedState = {
      ...state,
      globalFilters,
      rowSelection,
      sorting,
      expanded,
      editing
    };

    if (manualPagination) {
      mergedState.pagination = pagination;
    }

    return mergedState;
  }, [globalFilters, rowSelection, pagination, sorting, state, expanded, editing]);

  const mPaginationProps = useMemo(() => {
    return Object.assign({}, defaultPagination, paginationProps);
  }, [defaultPagination, paginationProps]);

  const defaultTableConfig = useMemo(() => {
    const config = {
      data: tableData,
      columns: columns.filter(column => {
        if (!column.screen) return column;

        const isVisible = column.screen.some(s => {
          return screens[s];
        });
        if (isVisible) return true;
        return false;
      }),
      pageCount,
      state: defaultState,
      defaultColumn: getDefaultColumn({ components }),
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      getRowId,
      onGlobalFilterChange: setGlobalFilters,
      onExpandedChange: setExpanded,
      onEditingChange: setEditing,
      getSubRows: row => row.subRows,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      autoResetPageIndex: false,
      manualPagination,
      enableRowSelection,
      editMode,
      meta: {
        updateData: (originalRow, columnId, value, isRowMode) => {
          if (isRowMode) {
            // For row edit mode, we'll handle the update in handleSave
            return;
          }
          updateData(originalRow, columnId, value, isRowMode);
        },
        handleSave,
        handleCancel,
        handleEdit
      }
    };

    if (!manualPagination) {
      config.getPaginationRowModel = getPaginationRowModel();
    } else {
      config.onPaginationChange = setPrePagination => {
        const prePagination = setPrePagination(pagination);

        if (prePagination.pageIndex >= 0) {
          setPagination(prePagination);
        }
      };
    }

    if (useGlobalFuzzyFilter) {
      config.globalFilterFn = fuzzyFilter;
    }

    return config;
  }, [manualPagination, defaultState, tableData, handleSave, handleCancel, handleEdit]);

  const table = useReactTable(defaultTableConfig);

  const isEmpty = !loading && !tableData.length;

  const dataIds = React.useMemo(() => data?.map(getRowId), [data]);

  const getTable = () => {
    return (
      <>
        <S.TableWrapper>
          <S.Table>
            <Thead table={table} components={components} />
            <TBody
              dataIds={dataIds}
              onRowClick={onRowClick}
              loading={loading}
              table={table}
              rowHeight={rowHeight}
              renderSubRow={renderSubRow}
            />
          </S.Table>
        </S.TableWrapper>
        {mPaginationProps.enabled && (
          <Pagination {...mPaginationProps} loading={loading} table={table} />
        )}
      </>
    );
  };

  const hasGlobalFilters = Object.keys(clearObject(globalFilters?.filters))?.length > 0;
  const getEmptyTable = () => {
    if (components?.EmptyTable && !hasGlobalFilters) {
      return <components.EmptyTable table={table} />;
    }
    return (
      <EmptyTableContent hasDescription={!!globalFilters?.searchTerm} {...emptyTableContent} />
    );
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setRearrangeData(data => {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);
        return arrayMove(data, oldIndex, newIndex); //this is just a splice util
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <TranslationContext.Provider value={translations}>
        {sectionHeaderProps.enabled && (
          <S.TableSectionHeader>
            <S.SectionHeaderLeft>
              <Typography variant="body2">{sectionHeaderProps.title}</Typography>
              <VerticalDivider $width={1} $height={20} />
              {sectionHeaderProps.cta}
            </S.SectionHeaderLeft>
            <S.SectionHeaderRight>
              <SearchInput
                {...searchProps}
                {...sectionHeaderProps.searchProps}
                onChange={searchTerm => {
                  if (setGlobalFilters)
                    setGlobalFilters(passValues => ({ ...passValues, searchTerm }));
                  table.resetPageIndex();
                }}
              />
            </S.SectionHeaderRight>
          </S.TableSectionHeader>
        )}
        <S.Wrapper isEmpty={isEmpty} ref={tableRef}>
          {enabled.header && (
            <Header
              tableRef={tableRef}
              table={table}
              components={components}
              globalFilters={globalFilters}
              filterOptions={filterOptions}
              setGlobalFilters={setGlobalFilters}
              onFilterChange={filters => {
                if (setGlobalFilters) setGlobalFilters(passValues => ({ ...passValues, filters }));
                table.resetPageIndex();
              }}
              setSearchTerm={searchTerm => {
                if (setGlobalFilters)
                  setGlobalFilters(passValues => ({ ...passValues, searchTerm }));
                table.resetPageIndex();
              }}
              enableFilter={enabled.filter}
              enableSearch={enabled.search}
              filterProps={filterProps}
              searchProps={searchProps}
            />
          )}
          {isEmpty ? getEmptyTable() : getTable()}
        </S.Wrapper>
      </TranslationContext.Provider>
    </DndContext>
  );
}

Table.S = S;

Table.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(columnTypes)
    })
  ),
  updateData: PropTypes.func,
  setRearrangeData: PropTypes.func,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func,
  setPagination: PropTypes.func,
  pagination: PropTypes.object,
  pageCount: PropTypes.number,
  state: PropTypes.object,
  globalFilters: PropTypes.object,
  manualPagination: PropTypes.bool,
  components: PropTypes.object,
  setGlobalFilters: PropTypes.func,
  filterOptions: PropTypes.array,
  useGlobalFuzzyFilter: PropTypes.bool,
  rowHeight: PropTypes.number,
  enabled: PropTypes.object,
  filterProps: PropTypes.object,
  searchProps: PropTypes.object,
  paginationProps: PropTypes.object,
  getRowId: PropTypes.func,
  translations: PropTypes.object,
  onRowClick: PropTypes.func,
  enableRowSelection: PropTypes.bool,
  tableRef: PropTypes.object,
  emptyTableContent: PropTypes.object,
  renderSubRow: PropTypes.func,
  editMode: PropTypes.oneOf(['inline', 'row']),
  sectionHeaderProps: PropTypes.shape({
    enabled: PropTypes.bool,
    title: PropTypes.string,
    cta: PropTypes.node,
    searchProps: PropTypes.object
  })
};

export { Table };
